import { useState, useRef, useEffect } from "react";
import { X, Send, Sparkles, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";

const SESSION_KEY = "eq_gabriel_session";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `gab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const WELCOME = {
  role: "assistant",
  content:
    "Bonjour 👋 Je suis Gabriel, l'assistant virtuel d'Equinoxe Propreté. Comment puis-je vous aider ?",
};

const renderRich = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

export const GabrielChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "" });
  const [leadLoading, setLeadLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const dismissed = useRef(false);
  const endRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!dismissed.current) setOpen(true);
    }, 2000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    dismissed.current = true;
    setOpen(false);
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Désolé, je rencontre un souci technique. Vous pouvez nous appeler au 05 32 18 90 44." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "Quels sont vos services ?",
    "Intervenez-vous à Cahors ?",
    "Je veux un devis",
    "J'ai besoin de tondre mon jardin",
    "Mes vitres sont sales",
    "Ma maison a besoin d'un bon coup de balai",
    "Ma voiture ne ressemble plus à rien !",
  ];

  const setLeadField = (k) => (e) => setLead({ ...lead, [k]: e.target.value });

  const submitLead = async (e) => {
    e.preventDefault();
    if (leadLoading) return;
    setLeadLoading(true);
    try {
      const { data } = await api.post("/chat-lead", { session_id: sessionId.current, ...lead });
      const name = lead.name;
      setShowLead(false);
      setLead({ name: "", email: "", phone: "", message: "" });
      const tail = data.confirmation_sent
        ? " Vous allez recevoir une confirmation par email, et nous vous recontactons très rapidement."
        : " Un conseiller vous recontactera très rapidement.";
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Merci ${name} ! 🙌 Votre message a bien été transmis à un conseiller.${tail}`,
        },
      ]);
    } catch {
      setShowLead(false);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Désolé, l'envoi a échoué. Vous pouvez réessayer ou nous appeler au 05 32 18 90 44." },
      ]);
    } finally {
      setLeadLoading(false);
    }
  };

  const leadInputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0ea5e9] focus:ring-2 focus:ring-sky-100 outline-none text-sm";

  return (
    <>
      {!open && (
        <button
          data-testid="gabriel-open-btn"
          onClick={() => setOpen(true)}
          aria-label="Discuter avec Gabriel"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#0ea5e9] hover:bg-[#0284c7] text-white pl-3 pr-4 py-3 rounded-full shadow-lg hover:scale-105 transition-transform duration-200"
        >
          <span className="relative flex w-9 h-9 items-center justify-center bg-white/20 rounded-full">
            <Sparkles size={18} />
          </span>
          <span className="text-sm font-bold hidden sm:block">Demander à Gabriel</span>
        </button>
      )}

      {open && (
        <div
          data-testid="gabriel-panel"
          className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[32rem] max-h-[70vh] bg-white rounded-3xl shadow-[0_20px_60px_rgba(14,165,233,0.25)] border border-slate-100 flex flex-col overflow-hidden"
        >
          <div className="bg-[#0ea5e9] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Sparkles size={20} /></span>
              <div>
                <p className="font-bold leading-tight">Gabriel</p>
                <p className="text-xs text-white/80">Assistant Propreté · en ligne</p>
              </div>
            </div>
            <button data-testid="gabriel-close-btn" onClick={close} aria-label="Fermer" className="p-1 hover:bg-white/10 rounded-lg"><X size={20} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0ea5e9] text-white rounded-br-md"
                      : "bg-white text-slate-700 border border-slate-100 rounded-bl-md"
                  }`}
                >
                  {renderRich(m.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 px-4 py-3 rounded-2xl rounded-bl-md flex gap-1">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className="w-2 h-2 bg-sky-300 rounded-full animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}
            {messages.length === 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => { setInput(s); }} className="text-xs bg-white border border-sky-100 text-sky-700 px-3 py-1.5 rounded-full hover:bg-sky-50 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {showLead ? (
            <form onSubmit={submitLead} data-testid="gabriel-lead-form" className="p-3 border-t border-slate-100 space-y-2 bg-white">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">Laisser un message à un conseiller</p>
                <button type="button" onClick={() => setShowLead(false)} aria-label="Annuler" className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
              </div>
              <input required data-testid="lead-name" value={lead.name} onChange={setLeadField("name")} placeholder="Votre nom *" className={leadInputCls} />
              <input required type="email" data-testid="lead-email" value={lead.email} onChange={setLeadField("email")} placeholder="Votre email *" className={leadInputCls} />
              <input data-testid="lead-phone" value={lead.phone} onChange={setLeadField("phone")} placeholder="Téléphone (facultatif)" className={leadInputCls} />
              <textarea required rows={2} data-testid="lead-message" value={lead.message} onChange={setLeadField("message")} placeholder="Votre message *" className={leadInputCls} />
              <button type="submit" disabled={leadLoading} data-testid="lead-submit" className="w-full bg-[#0ea5e9] hover:bg-[#0284c7] disabled:opacity-60 text-white py-2.5 rounded-full font-bold text-sm transition-colors">
                {leadLoading ? "Envoi…" : "Envoyer au conseiller"}
              </button>
            </form>
          ) : (
            <>
              <button
                data-testid="gabriel-lead-open"
                onClick={() => setShowLead(true)}
                className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-[#0284c7] bg-sky-50 hover:bg-sky-100 py-2.5 border-t border-slate-100 transition-colors"
              >
                <ClipboardList size={14} /> Laisser un message à un conseiller
              </button>
              <form onSubmit={send} className="p-3 border-t border-slate-100 flex items-center gap-2">
                <input
                  data-testid="gabriel-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Écrivez votre message…"
                  className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 focus:border-[#0ea5e9] focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                />
                <button data-testid="gabriel-send-btn" type="submit" disabled={loading || !input.trim()} className="w-10 h-10 flex items-center justify-center bg-[#0ea5e9] hover:bg-[#0284c7] disabled:opacity-50 text-white rounded-full transition-colors">
                  <Send size={17} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};
